<template>
  <BMDialog
    width="600px"
    :showDialog="Boolean(globalDialog)"
    :fullscreen="false"
    :showFooter="false"
    :showDivider="false"
    class="global-dialog"
    @closed="triggerAction(false)"
    @confirmed="triggerAction(true)"
    dialogTestId="global-dialog"
    :dialogStatus="globalDialog?.type ?? null"
  >
    <div v-if="globalDialog" class="text-center overflow-y-hidden">
      <div
        v-if="notificationIcon"
        :class="[
          `notification-icon-container rounded-circle d-flex align-center justify-center mx-auto mb-5`,
          `bg-${notificationIcon.color}-50`
        ]"
      >
        <v-icon :color="notificationIcon.color" class="ic-32">
          {{ notificationIcon.icon }}
        </v-icon>
      </div>
      <div
        v-if="globalDialog.msg"
        v-sanitize="globalDialog.msg"
        class="text-h5 mb-4"
      ></div>
      <div
        v-if="globalDialog.description"
        v-sanitize="globalDialog.description"
        class="text-subtitle-1 mb-4"
      ></div>

      <div class="d-flex justify-center ga-3">
        <BMButton
          v-if="cancelText"
          :title="cancelText"
          variant="tonal"
          color="secondary"
          data-cc="global-dialog-cancel-button"
          @clicked="triggerAction(false)"
        />
        <BMButton
          v-if="hasSubmitButton"
          :title="submitText"
          data-cc="global-dialog-confirm-button"
          @clicked="triggerAction(true)"
        />
      </div>
    </div>
  </BMDialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useI18n } from 'vue-i18n';

import BMButton from '@/components/common/utilities/BMButton.vue';
import BMDialog from '@/components/common/utilities/BMDialog.vue';

import useDialogsStore from '@/stores/dialogs';
import type { GlobalDialogData, GlobalDialogType } from '@/types/dialogs';

interface NotificationIcon {
  icon: string;
  color: string;
}

const NOTIFICATION_ICONS: Record<GlobalDialogType, NotificationIcon> = {
  success: { icon: 'huge:checkmark-circle', color: 'success' },
  error: { icon: 'huge:alert-circle', color: 'error' },
  info: { icon: 'huge:information-circle', color: 'info' },
  warning: { icon: 'huge:alert-circle', color: 'warning' }
};

const { t } = useI18n();

const dialogsStore = useDialogsStore();

const globalDialog = computed<GlobalDialogData | null>(
  () => dialogsStore.globalDialog
);

const hasSubmitButton = computed<boolean>(
  () => typeof globalDialog.value?.callback !== 'undefined'
);

const notificationIcon = computed<NotificationIcon | undefined>(() => {
  if (!globalDialog.value) return undefined;
  const defaultIcon = NOTIFICATION_ICONS[globalDialog.value.type];
  if (!defaultIcon) return undefined;
  return {
    icon: globalDialog.value.icon ?? defaultIcon.icon,
    color: defaultIcon.color
  };
});

const submitText = computed<string>(
  () => globalDialog.value?.submitText ?? t('generic.save')
);

const cancelText = computed<string | null>(
  () => globalDialog.value?.cancelText ?? null
);

const triggerAction = (isSubmit: boolean): void => {
  if (globalDialog.value?.callback) globalDialog.value.callback(isSubmit);
  dialogsStore.toggleGlobalDialog(null);
};
</script>

<style scoped lang="scss">
.notification-icon-container {
  width: 64px;
  height: 64px;
}
</style>
